import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";

const emptyProduct = {
  name: "", slug: "", category_slug: "necklaces", price: 0, compare_at_price: null,
  short_description: "", description: "", images: [], stock: 0, sku: "",
  tags: [], materials: "", sizes: [], is_active: true,
  seo_title: "", seo_description: "", theme_slug: "", bundle_product_ids: [],
  variant_group: "",
};

const THEME_OPTIONS = [
  { value: "", label: "— None (default PDP) —" },
  { value: "rapunzel", label: "Rapunzel (golden lantern edit)" },
];

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/admin/products").then(r => setProducts(r.data)).catch(() => {});
    api.get("/categories").then(r => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...emptyProduct, category_slug: cats[0]?.slug || "necklaces" }); setOpen(true); };
  const openEdit = (p) => { setEditing({ ...p, tags: p.tags || [], sizes: p.sizes || [], images: p.images || [] }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    try {
      const body = { ...editing, price: Number(editing.price), stock: Number(editing.stock || 0), compare_at_price: editing.compare_at_price ? Number(editing.compare_at_price) : null };
      if (editing.id) {
        await api.patch(`/admin/products/${editing.id}`, body);
      } else {
        await api.post(`/admin/products`, body);
      }
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((ed) => ({ ...ed, images: [...(ed.images || []), data.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cloudinary not configured. Paste image URLs manually for now.");
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <p className="eyebrow mb-1">Catalog</p>
          <h1 className="font-hero text-3xl">Products</h1>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="admin-new-product-button">
          <Plus size={14} className="mr-2" /> New product
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="text-left font-label text-[11px] uppercase tracking-[0.22em] text-charcoal/70 border-b border-border">
              <th className="py-3 px-4">Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th className="text-right px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(p => (
              <tr key={p.id} className="border-b border-border last:border-b-0" data-testid={`admin-product-row-${p.slug}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-12 rounded-md bg-blush/40 overflow-hidden flex-shrink-0">{p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                    <div>
                      <p className="font-cormorant text-lg leading-tight">{p.name}</p>
                      <p className="text-xs text-charcoal/70">{p.sku || p.slug}</p>
                    </div>
                  </div>
                </td>
                <td className="capitalize">{p.category_slug}</td>
                <td className="font-body">{formatINR(p.price)}</td>
                <td><span className={`px-2 py-1 rounded-full text-[10px] font-label uppercase tracking-[0.2em] ${p.stock < 5 ? "bg-blush text-rose" : "bg-lavender text-deeplav"}`}>{p.stock}</span></td>
                <td>{p.is_active ? <span className="text-emerald-600 font-label text-xs uppercase tracking-[0.2em]">Live</span> : <span className="text-charcoal/70 font-label text-xs uppercase tracking-[0.2em]">Hidden</span>}</td>
                <td className="text-right px-4">
                  <button className="p-2 text-charcoal/70 hover:text-charcoal" onClick={() => openEdit(p)} aria-label="edit" data-testid={`admin-edit-${p.slug}`}><Pencil size={14} /></button>
                  <button className="p-2 text-charcoal/70 hover:text-rose" onClick={() => remove(p)} aria-label="delete" data-testid={`admin-delete-${p.slug}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-cream">
          <DialogHeader>
            <DialogTitle className="font-hero text-2xl">{editing?.id ? "Edit product" : "New product"}</DialogTitle>
            <DialogDescription>Keep it editorial — short, warm, specific.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow block mb-1">Name</label>
                  <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Slug</label>
                  <Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Category</label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={editing.category_slug} onChange={e => setEditing({ ...editing, category_slug: e.target.value })}>
                    {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-1">SKU</label>
                  <Input value={editing.sku || ""} onChange={e => setEditing({ ...editing, sku: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Price (INR)</label>
                  <Input type="number" value={editing.price} onChange={e => setEditing({ ...editing, price: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Compare at price (optional)</label>
                  <Input type="number" value={editing.compare_at_price || ""} onChange={e => setEditing({ ...editing, compare_at_price: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Stock</label>
                  <Input type="number" value={editing.stock} onChange={e => setEditing({ ...editing, stock: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Materials</label>
                  <Input value={editing.materials || ""} onChange={e => setEditing({ ...editing, materials: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1">Short description</label>
                <Input value={editing.short_description || ""} onChange={e => setEditing({ ...editing, short_description: e.target.value })} />
              </div>
              <div>
                <label className="eyebrow block mb-1">Description</label>
                <Textarea rows={5} value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow block mb-1">Tags (comma separated)</label>
                  <Input value={(editing.tags || []).join(", ")} onChange={e => setEditing({ ...editing, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} placeholder="featured, new, best_seller" />
                </div>
                <div>
                  <label className="eyebrow block mb-1">Sizes (comma separated)</label>
                  <Input value={(editing.sizes || []).join(", ")} onChange={e => setEditing({ ...editing, sizes: e.target.value.split(",").map(t => t.trim()).filter(Boolean) })} />
                </div>
              </div>

              {/* Quick visibility toggles */}
              <div className="rounded-xl border border-border bg-blush/20 p-4" data-testid="product-visibility-panel">
                <p className="eyebrow mb-3">Where this product shows</p>
                <div className="space-y-2.5 text-sm">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-featured">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-rose"
                      checked={(editing.tags || []).includes("featured")}
                      onChange={(e) => {
                        const tags = new Set(editing.tags || []);
                        if (e.target.checked) tags.add("featured"); else tags.delete("featured");
                        setEditing({ ...editing, tags: Array.from(tags) });
                      }}
                    />
                    <span><strong>Feature on homepage</strong> <span className="text-charcoal/60">— show this product in the "Featured" section of the home page.</span></span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-new">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-rose"
                      checked={(editing.tags || []).includes("new")}
                      onChange={(e) => {
                        const tags = new Set(editing.tags || []);
                        if (e.target.checked) tags.add("new"); else tags.delete("new");
                        setEditing({ ...editing, tags: Array.from(tags) });
                      }}
                    />
                    <span><strong>Mark as new</strong> <span className="text-charcoal/60">— shows a "new" badge on the card.</span></span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-bestseller">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-rose"
                      checked={(editing.tags || []).includes("best_seller")}
                      onChange={(e) => {
                        const tags = new Set(editing.tags || []);
                        if (e.target.checked) tags.add("best_seller"); else tags.delete("best_seller");
                        setEditing({ ...editing, tags: Array.from(tags) });
                      }}
                    />
                    <span><strong>Best seller</strong> <span className="text-charcoal/60">— eligible for the "Best sellers" section.</span></span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="toggle-active">
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-rose"
                      checked={editing.is_active !== false}
                      onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                    />
                    <span><strong>Active</strong> <span className="text-charcoal/60">— uncheck to hide the product everywhere (admin-only draft).</span></span>
                  </label>
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-2">Images</label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {(editing.images || []).map((img, i) => (
                    <div key={i} className="relative w-20 h-24 rounded-lg overflow-hidden bg-blush/40">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button className="absolute top-1 right-1 bg-white rounded-full w-5 h-5 text-[10px]" onClick={() => setEditing({ ...editing, images: editing.images.filter((_, j) => j !== i) })}>×</button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="btn-outline cursor-pointer">
                    <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Upload (Cloudinary)"}
                    <input type="file" className="hidden" accept="image/*" onChange={onUpload} disabled={uploading} />
                  </label>
                  <Input placeholder="Or paste an image URL and press Enter" onKeyDown={e => {
                    if (e.key === "Enter" && e.target.value) { setEditing({ ...editing, images: [...(editing.images || []), e.target.value] }); e.target.value = ""; e.preventDefault(); }
                  }} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="eyebrow block mb-1">SEO title</label>
                  <Input value={editing.seo_title || ""} onChange={e => setEditing({ ...editing, seo_title: e.target.value })} />
                </div>
                <div>
                  <label className="eyebrow block mb-1">SEO description</label>
                  <Input value={editing.seo_description || ""} onChange={e => setEditing({ ...editing, seo_description: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="eyebrow block mb-1">Character theme <span className="normal-case tracking-normal text-charcoal/60">(enables a fully themed PDP)</span></label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={editing.theme_slug || ""}
                  onChange={e => setEditing({ ...editing, theme_slug: e.target.value })}
                  data-testid="admin-product-theme-select"
                >
                  {THEME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {editing.theme_slug && (
                  <p className="mt-2 text-xs text-rose">
                    This product will render the <strong>{editing.theme_slug}</strong> themed PDP layout with character visuals and narrative.
                  </p>
                )}
              </div>

              <div>
                <label className="eyebrow block mb-1">
                  Design variant group <span className="normal-case tracking-normal text-charcoal/60">(links sibling designs of the same product)</span>
                </label>
                <Input
                  value={editing.variant_group || ""}
                  onChange={e => setEditing({ ...editing, variant_group: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                  placeholder="e.g. tulip-bracelet-collection"
                  data-testid="admin-product-variant-group"
                />
                <p className="mt-2 text-xs text-charcoal/60">
                  Set the same value on every design variant (e.g. all 4 tulip bracelets share <code className="bg-blush/40 px-1.5 py-0.5 rounded">tulip-bracelet-collection</code>).
                  A "More designs in this collection" picker then appears on each product's PDP. Leave empty for standalone products.
                </p>
              </div>

              <div>
                <label className="eyebrow block mb-1">
                  Frequently bought together <span className="normal-case tracking-normal text-charcoal/60">(up to 2 — leave empty to auto-suggest)</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2" data-testid="admin-bundle-chips">
                  {(editing.bundle_product_ids || []).map((pid) => {
                    const prod = products.find(p => p.id === pid);
                    return (
                      <span key={pid} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blush/60 text-charcoal text-xs border border-border">
                        {prod ? prod.name : pid.slice(0, 8)}
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, bundle_product_ids: editing.bundle_product_ids.filter(x => x !== pid) })}
                          className="text-charcoal/70 hover:text-rose"
                          aria-label="remove"
                        >×</button>
                      </span>
                    );
                  })}
                </div>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value=""
                  onChange={e => {
                    if (!e.target.value) return;
                    const list = editing.bundle_product_ids || [];
                    if (list.length >= 2 || list.includes(e.target.value) || e.target.value === editing.id) return;
                    setEditing({ ...editing, bundle_product_ids: [...list, e.target.value] });
                  }}
                  data-testid="admin-bundle-add-select"
                >
                  <option value="">— Add a product to the bundle —</option>
                  {products
                    .filter(p => p.id !== editing.id && !(editing.bundle_product_ids || []).includes(p.id))
                    .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 font-label text-[12px] uppercase tracking-[0.2em] text-charcoal">
                <input type="checkbox" checked={editing.is_active} onChange={e => setEditing({ ...editing, is_active: e.target.checked })} className="accent-rose" />
                Active & visible in storefront
              </label>
              <div className="flex justify-end gap-3 pt-3">
                <button className="btn-outline" onClick={close}>Cancel</button>
                <button className="btn-primary" onClick={save} data-testid="admin-save-product">Save product</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
